import { track } from "@amplitude/analytics-react-native";
import { useApolloClient } from "@apollo/client";
import { useCallback } from "react";
import CreateCommentDocument from "src/api/graphql/__generated__/documents/CreateCommentDocument";
import GatheringNumCommentsFragmentFragmentDoc from "src/api/graphql/__generated__/documents/GatheringNumCommentsFragmentFragmentDoc";
import PhotoNumCommentsFragmentFragmentDoc from "src/api/graphql/__generated__/documents/PhotoNumCommentsFragmentFragmentDoc";
import parseInlineLinkText from "src/components/InlineLinkText/helpers/parseInlineLinkText";
import { getClientId } from "src/helpers/globalHelpers";
import haptic from "src/helpers/haptic";
import log from "src/helpers/log";
import { getCurTime_MS } from "src/shared/helpers/timeHelpers";
import useCommentsThreadStore from "./useCommentsThreadStore";

const usePostComment = () => {
	const threadSpecifier = useCommentsThreadStore((store) => store.threadSpecifier);
	const topic = useCommentsThreadStore((store) => store.topic);
	const apolloClient = useApolloClient();
	const createOptimisticComment = useCommentsThreadStore(
		(store) => store.createOptimisticComment,
	);

	const postComment = useCallback(
		async (contentInput: string) => {
			const clientId = getClientId();
			const content = contentInput.trim();
			if (content.length === 0) return;
			const { deleteOptimisticComment, migrateOptimisticCommentId } = createOptimisticComment(
				{
					content,
					creatorUserId: clientId,
					postedAt: getCurTime_MS(),
					threadReplyId: threadSpecifier.photoId,
				},
			);

			try {
				haptic("Light");
				const { data } = await apolloClient.mutate({
					mutation: CreateCommentDocument,
					variables: {
						input: {
							...threadSpecifier,
							content,
						},
					},
				});
				if (data?.createComment?.commentId) {
					const parsedText = parseInlineLinkText(content);
					let parsedInlineLinkTextLength = 0;
					for (const part of parsedText) {
						parsedInlineLinkTextLength += part.text.length;
					}
					let numTags = 0;
					for (const part of parsedText) {
						if (part.type === "link" && part.url.startsWith("/user_/")) {
							numTags++;
						}
					}

					track("comment_post", {
						gatheringId: threadSpecifier.gatheringId,
						photoId: threadSpecifier.photoId,
						contentLength: content.length,
						parsedInlineLinkTextLength,
						numTags,
					});

					migrateOptimisticCommentId(data.createComment.commentId);
					topic.emit("onCreateCommentSuccess", {
						commentId: data.createComment.commentId,
						threadSpecifier,
					});
					if (threadSpecifier.gatheringId) {
						const cacheResult = apolloClient.cache.readFragment({
							fragment: GatheringNumCommentsFragmentFragmentDoc,
							id: apolloClient.cache.identify({
								__typename: "Gathering",
								gatheringId: threadSpecifier.gatheringId,
							}),
						});
						apolloClient.cache.writeFragment({
							fragment: GatheringNumCommentsFragmentFragmentDoc,
							id: apolloClient.cache.identify({
								__typename: "Gathering",
								gatheringId: threadSpecifier.gatheringId,
							}),
							data: {
								...cacheResult,
								gatheringId: threadSpecifier.gatheringId,
								numComments: (cacheResult?.numComments ?? 0) + 1,
							},
						});
					}
					if (threadSpecifier.photoId) {
						const cacheResult = apolloClient.cache.readFragment({
							fragment: PhotoNumCommentsFragmentFragmentDoc,
							id: apolloClient.cache.identify({
								__typename: "Photo",
								photoId: threadSpecifier.photoId,
							}),
						});
						apolloClient.cache.writeFragment({
							fragment: PhotoNumCommentsFragmentFragmentDoc,
							id: apolloClient.cache.identify({
								__typename: "Photo",
								photoId: threadSpecifier.photoId,
							}),
							data: {
								...cacheResult,
								photoId: threadSpecifier.photoId,
								numComments: (cacheResult?.numComments ?? 0) + 1,
							},
						});
					}
				}
			} catch (err) {
				deleteOptimisticComment();
				log.error({ err }, "Error posting comment");
			}
		},
		[apolloClient, createOptimisticComment, threadSpecifier, topic],
	);

	return {
		postComment,
	};
};
export default usePostComment;
